import type { PaginatedResponse } from "../zekay/pagination";
import type {
  LoginForm,
  UserWithRole,
  UpdateUserProfilePayload,
  CreateDirectoryUserPayload,
  ActivateUserAccountPayload,
  UserDocument,
  UserDocumentPatchPayload,
  UserDocumentsBatchItem,
  UserDocumentsAlertBatchItem,
  UserDocumentsAlertBatchResponseItem,
  UserDisplayBatchItem,
  User,
} from "./User.typed";
import type { UserModel } from "./UserModel";

export class UserAdapter {
  userModel: UserModel;

  constructor(userModel: UserModel) {
    this.userModel = userModel;
  }

  async getUsers(employeesOnly = false): Promise<UserWithRole[]> {
    return this.userModel.getUsers(employeesOnly);
  }

  async getUsersDisplayBatch(ids: number[]): Promise<UserDisplayBatchItem[]> {
    return this.userModel.getUsersDisplayBatch(ids);
  }

  async createDirectoryUser(payload: CreateDirectoryUserPayload): Promise<User> {
    return this.userModel.createDirectoryUser(payload);
  }

  async prepareUserActivation(id: number, payload: ActivateUserAccountPayload): Promise<void> {
    return this.userModel.prepareUserActivation(id, payload);
  }

  async sendUserInvitationMail(id: number): Promise<void> {
    return this.userModel.sendUserInvitationMail(id);
  }

  async createInvitedUser(form: LoginForm): Promise<void> {
    return this.userModel.createInvitedUser(form);
  }

  async deleteUser(id: number): Promise<void> {
    return this.userModel.deleteUser(id);
  }

  async updateUserRole(id: number, roleId: number): Promise<void> {
    return this.userModel.updateUserRole(id, roleId);
  }

  async updateUserProfile(id: number, payload: UpdateUserProfilePayload): Promise<void> {
    return this.userModel.updateUserProfile(id, payload);
  }

  async getUserDocuments(
    userId: number,
    params?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<UserDocument>> {
    return this.userModel.getUserDocuments(userId, params);
  }

  async getUserDocumentsBatch(
    items: UserDocumentsBatchItem[],
  ): Promise<PaginatedResponse<UserDocument>[]> {
    return this.userModel.getUserDocumentsBatch(items);
  }

  async getUserDocumentsAlertBatch(
    items: UserDocumentsAlertBatchItem[],
  ): Promise<UserDocumentsAlertBatchResponseItem[]> {
    return this.userModel.getUserDocumentsAlertBatch(items);
  }

  async uploadUserDocument(userId: number, formData: FormData): Promise<UserDocument> {
    return this.userModel.uploadUserDocument(userId, formData);
  }

  async patchUserDocument(
    userId: number,
    documentId: number,
    payload: UserDocumentPatchPayload,
  ): Promise<UserDocument> {
    return this.userModel.patchUserDocument(userId, documentId, payload);
  }

  async deleteUserDocument(userId: number, documentId: number): Promise<void> {
    return this.userModel.deleteUserDocument(userId, documentId);
  }
}
